import { useReveal, useParallax } from "../hooks/useReveal";
import { useLanguage } from "../context/LanguageContext";

// Editorial "Born in Los Cabos" section — brand origin storytelling.
const IMG_ARCH_SUNSET = "/images/cabo-arch.webp";
const IMG_AERIAL_BEACH = "/images/cabo-aerial.webp";
const IMG_MARINA = "/images/cabo-marina.webp";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, revealed] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Parallax = ({ speed = 0.15, children, className = "" }) => {
  const [ref, offset] = useParallax(speed);
  return (
    <div
      ref={ref}
      className={className}
      style={{ transform: `translate3d(0, ${offset}px, 0)`, willChange: "transform" }}
    >
      {children}
    </div>
  );
};

export const CaboSection = () => {
  const { t } = useLanguage();
  const c = t.caboSection;

  return (
    <section
      id="cabo"
      data-testid="cabo-section"
      className="relative bg-black text-white overflow-hidden"
    >
      {/* 1. Cinematic full-bleed opener — The Arch at sunset */}
      <div className="relative h-[85vh] md:h-[100vh] overflow-hidden">
        <Parallax speed={0.25} className="absolute inset-0">
          <img
            src={IMG_ARCH_SUNSET}
            alt="El Arco de Cabo San Lucas"
            data-testid="cabo-hero-image"
            className="w-full h-[130%] object-cover image-cinematic"
            style={{ objectPosition: "50% 45%" }}
            loading="lazy"
          />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-transparent" />

        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 flex items-end pb-16 md:pb-24">
          <div className="max-w-3xl">
            <Reveal>
              <p className="overline mb-5">{c.chapterOverline}</p>
            </Reveal>
            <Reveal delay={120}>
              <h2
                data-testid="cabo-title"
                className="h-display text-white text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.85]"
              >
                {c.titleLine1}
                <br />
                <span className="text-yellow-400">{c.titleLine2}</span>
              </h2>
            </Reveal>
            <Reveal delay={280}>
              <p
                data-testid="cabo-subtitle"
                className="mt-8 text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed"
              >
                {c.subtitle}
              </p>
            </Reveal>
            <Reveal delay={420}>
              <div
                data-testid="cabo-coordinates"
                className="mt-10 inline-flex items-center gap-4 border-t border-white/25 pt-4"
              >
                <span className="w-1.5 h-1.5 bg-yellow-400" aria-hidden />
                <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.4em] text-white/85 tabular-nums">
                  Los Cabos · BCS · MX
                </span>
                <span className="w-px h-3 bg-white/30" aria-hidden />
                <span className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-white/60 tabular-nums">
                  22.89° N · 109.92° W
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* 2. Copy + Image split — Aerial beach as portrait companion */}
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-6 space-y-8 text-lg leading-relaxed text-white/75 order-2 lg:order-1">
          <Reveal>
            <p>{c.para1}</p>
          </Reveal>
          <Reveal delay={120}>
            <p>{c.para2}</p>
          </Reveal>
          <Reveal delay={240}>
            <p className="font-display text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight text-white pt-4">
              {c.quoteLine1}
              <br />
              <span className="text-yellow-400">{c.quoteLine2}</span>
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <Reveal delay={100}>
            <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
              <Parallax speed={0.12} className="absolute inset-0">
                <img
                  src={IMG_AERIAL_BEACH}
                  alt={c.aerialLabel}
                  data-testid="cabo-aerial-image"
                  className="w-full h-[115%] object-cover image-cinematic"
                  style={{ objectPosition: "48% 50%" }}
                  loading="lazy"
                />
              </Parallax>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/15" />
              <div className="absolute bottom-4 left-4 flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-yellow-400" aria-hidden />
                <span className="overline text-white">{c.aerialLabel}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* 3. Panoramic band — resilience story */}
      <div className="pb-24 md:pb-32">
        <Reveal>
          <div className="relative w-full overflow-hidden group min-h-[70vh] md:h-[85vh] max-h-none md:max-h-[880px]">
            <Parallax speed={0.18} className="absolute inset-0">
              <img
                src={IMG_MARINA}
                alt="Cabo San Lucas Marina"
                data-testid="cabo-marina-image"
                className="w-full h-[120%] object-cover image-cinematic"
                style={{ objectPosition: "50% 55%" }}
                loading="lazy"
              />
            </Parallax>
            {/* Deeper vignette so the emotional quote sits with authority */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/35" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent" />

            <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 flex items-end pb-14 md:pb-20">
              <div className="max-w-2xl" data-testid="cabo-community-block">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-6 h-px bg-yellow-400" aria-hidden />
                  <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.4em] text-yellow-400">
                    {c.harborOverline}
                  </p>
                </div>
                <blockquote className="border-l-2 border-yellow-400 pl-6 md:pl-8">
                  <p className="font-editorial text-2xl md:text-3xl lg:text-[2.2rem] text-white leading-[1.35] tracking-tight italic">
                    &ldquo;{c.harborBody}&rdquo;
                  </p>
                </blockquote>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
